import { DOMProps } from '@react-types/shared';
import { filterDOMProps } from '@react-aria/utils';
import React, { ElementType, forwardRef } from 'react';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/text/text.module.css';
import {
  SapphireStyleProps,
  TextStyleProps,
  useTextStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';

export interface TypographyCaptionProps
  extends DOMProps,
    TextStyleProps,
    SapphireStyleProps,
    GlobalDomAttributes {
  children: React.ReactNode;
  /**
   * The actual DOM element to render.
   * @default 'div'
   */
  elementType?: string;
  /**
   * Sets the level of the hierarchy of the caption.
   * @default 'md'
   */
  size?: 'md' | 'sm';
  /**
   * The semantic color of the text
   * @default 'primary'
   */
  color?:
    | 'primary'
    | 'secondary'
    | 'positive'
    | 'negative'
    | 'warning'
    | 'informative';
}

/**
 * Caption text. Usually serves as a description of an adjecent picture or
 * other element.
 * By default, it renders as inline text (div), but you can render it as
 * something more semantically meaningful.
 *
 * See:
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element#text_content
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element#inline_text_semantics
 */
export const Caption = forwardRef(function Caption(
  props: TypographyCaptionProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  useThemeCheck();

  const {
    children,
    size = 'md',
    color = 'primary',
    elementType: CustomElement = 'div' as ElementType,
    ...otherProps
  } = props;
  const { styleProps } = useTextStyleProps(otherProps);
  const sizeClassName = styles[`sapphire-text--caption-${size}` as const];
  const colorClassName =
    color === 'primary'
      ? undefined
      : styles[`sapphire-text--${color}` as const];

  return (
    <CustomElement
      {...filterDOMProps(props, { global: true })}
      {...styleProps}
      className={clsx(
        styleProps.className,
        styles['sapphire-text'],
        sizeClassName,
        colorClassName
      )}
      style={styleProps.style}
      ref={ref}
    >
      {children}
    </CustomElement>
  );
});
